import type { KaizenRecord } from '../../../types/gainsMonitor';
import type { StagePerformance } from '../../../services/workflowService';
import {
  benefitInclusion,
  inclusionNote,
  operationalStatusLabel,
  type BenefitInclusion,
} from './benefitGovernance';
export type { BenefitInclusion };

export interface WaterfallStep {
  key: string;
  label: string;
  amount: number;
  pct: number;
}

export interface GainRankItem {
  id: string;
  label: string;
  sublabel?: string;
  amount: number;
}

export interface AreaSavingsRow {
  id: string;
  name: string;
  amount: number;
  pct: number;
}

export interface MonthlySavingsRow {
  key: string;
  label: string;
  economia: number;
  custo: number;
  liquido: number;
}

export function buildWaterfallSteps(
  totalSavings: number,
  metrics: { sla_rate_7d?: number; tasks_overdue?: number } | null | undefined,
  totalThroughput: number,
  avgLeadTimeHours: number,
  wipStageRows: { avg_wip: number }[],
): WaterfallStep[] {
  const slaScore = Math.max(0, (metrics?.sla_rate_7d ?? 0) - 70);
  const throughputScore = totalThroughput;
  const leadTimeScore = Math.max(0, 48 - avgLeadTimeHours) * 10;
  const capacityScore = wipStageRows.reduce((s, r) => s + Math.max(0, 20 - r.avg_wip), 0);
  const projectScore = Math.max(1, metrics?.tasks_overdue ?? 0) * 5;

  const weights = [
    { key: 'throughput', label: 'Throughput', weight: throughputScore },
    { key: 'sla', label: 'SLA', weight: slaScore * 2 },
    { key: 'leadtime', label: 'Lead Time', weight: leadTimeScore },
    { key: 'capacity', label: 'Capacidade', weight: capacityScore },
    { key: 'projects', label: 'Projetos', weight: projectScore },
  ];

  const sum = weights.reduce((s, w) => s + w.weight, 0) || 1;
  return weights.map((w) => ({
    key: w.key,
    label: w.label,
    amount: (totalSavings * w.weight) / sum,
    pct: (w.weight / sum) * 100,
  }));
}

/**
 * Estimativa heurística de apresentação. Não grava annual_cost_saved.
 * Ausência permanece indisponível. Meta não substitui a medição atual.
 */
export function estimateKaizenSavings(k: KaizenRecord): number | null {
  if (k.baseline_cost != null && k.current_cost != null) {
    const costDelta = k.baseline_cost - k.current_cost;
    if (costDelta > 0) return costDelta;
  }
  if (k.baseline_lead_time != null && k.current_lead_time != null) {
    const leadTimeDelta = k.baseline_lead_time - k.current_lead_time;
    if (leadTimeDelta > 0) return (leadTimeDelta / 60) * 50 * 12;
  }
  return null;
}

export function buildGainsRanking(
  kaizens: KaizenRecord[],
  waterfall: WaterfallStep[],
): GainRankItem[] {
  const fromKaizen = kaizens.flatMap((k): GainRankItem[] => {
    const amount = estimateKaizenSavings(k);
    if (amount == null || amount <= 0) return [];
    return [{
      id: k.id,
      label: k.title,
      sublabel: k.type.replace(/_/g, ' '),
      amount,
    }];
  }).sort((a, b) => b.amount - a.amount);

  if (fromKaizen.length >= 3) return fromKaizen.slice(0, 8);

  return waterfall
    .filter((w) => w.amount > 0)
    .sort((a, b) => b.amount - a.amount)
    .map((w) => ({
      id: w.key,
      label: w.label,
      sublabel: `${w.pct.toFixed(0)}% do total`,
      amount: w.amount,
    }))
    .slice(0, 8);
}

export function buildSavingsByArea(
  stages: StagePerformance[],
  totalSavings: number,
): AreaSavingsRow[] {
  if (stages.length === 0 || totalSavings <= 0) return [];

  const scored = stages.map((s) => ({
    id: s.stage_name,
    name: s.stage_name,
    weight: Math.max(1, s.avg_wip * Math.max(100 - s.sla_rate, 1)),
  }));
  const sum = scored.reduce((s, r) => s + r.weight, 0) || 1;

  return scored
    .map((r) => ({
      id: r.id,
      name: r.name,
      amount: (totalSavings * r.weight) / sum,
      pct: (r.weight / sum) * 100,
    }))
    .sort((a, b) => b.amount - a.amount)
    .slice(0, 8);
}

export function buildSavingsByProject(
  kaizens: KaizenRecord[],
  projectNames: Record<string, string> = {},
): AreaSavingsRow[] {
  const byProject = new Map<string, { name: string; amount: number }>();

  for (const k of kaizens) {
    const pid = k.project_id || 'sem-projeto';
    const savings = estimateKaizenSavings(k);
    if (savings == null || savings <= 0) continue;
    const name = pid === 'sem-projeto'
      ? 'Operação contínua'
      : projectNames[pid] || `Projeto ${pid.slice(0, 8)}…`;
    const cur = byProject.get(pid) ?? { name, amount: 0 };
    cur.amount += savings;
    byProject.set(pid, cur);
  }

  const rows = [...byProject.entries()]
    .map(([id, v]) => ({ id, name: v.name, amount: v.amount, pct: 0 }))
    .filter((row) => row.amount > 0)
    .sort((a, b) => b.amount - a.amount)
    .slice(0, 8);

  const max = rows[0]?.amount ?? 0;
  return rows.map((row) => ({ ...row, pct: max > 0 ? (row.amount / max) * 100 : 0 }));
}

export interface KaizenRoiAmountRow {
  kaizen_id: string;
  annual_cost_saved?: number | null;
  total_investment?: number | null;
}

/** Soma cada Kaizen uma vez, mesmo se a lista vier repetida pelo join de alertas. */
export function sumUniqueKaizenRoi(rows: KaizenRoiAmountRow[]): { annualSavings: number; realInvestment: number } {
  const byId = new Map<string, KaizenRoiAmountRow>();
  for (const row of rows) {
    if (!row.kaizen_id || byId.has(row.kaizen_id)) continue;
    byId.set(row.kaizen_id, row);
  }
  let annualSavings = 0;
  let realInvestment = 0;
  for (const row of byId.values()) {
    annualSavings += Number(row.annual_cost_saved ?? 0);
    realInvestment += Number(row.total_investment ?? 0);
  }
  return { annualSavings, realInvestment };
}

/**
 * Fallback histórico de R$ 3.500 por Kaizen.
 * O Painel não usa este valor para ROI ou payback.
 */
export const LEGACY_INVESTMENT_FALLBACK_PER_KAIZEN = 3500;

export function buildMonthlyEvolution(
  impactRows: Array<{ date?: string; economia?: number; custo?: number }>,
): MonthlySavingsRow[] {
  const byMonth = new Map<string, MonthlySavingsRow>();

  for (const row of impactRows) {
    if (!row.date) continue;
    const d = new Date(row.date);
    if (Number.isNaN(d.getTime())) continue;
    const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    const label = d.toLocaleDateString('pt-BR', { month: 'short', year: '2-digit' });
    const cur = byMonth.get(key) ?? { key, label, economia: 0, custo: 0, liquido: 0 };
    cur.economia += row.economia ?? 0;
    cur.custo += row.custo ?? 0;
    cur.liquido = cur.economia - cur.custo;
    byMonth.set(key, cur);
  }

  return [...byMonth.values()].sort((a, b) => a.key.localeCompare(b.key)).slice(-6);
}

/**
 * annual_cost_saved não tem data de realização.
 * calculated_at/updated_at registram a gravação, não uma série de ganho.
 */
export function hasBenefitTimeSeries(): boolean {
  return false;
}

export const KAIZEN_IMPROVEMENT_TYPE_LABEL: Record<string, string> = {
  reducao_lead_time: 'Redução de lead time',
  eliminacao_desperdicio: 'Eliminação de desperdício',
  automacao: 'Automação',
  padronizacao: 'Padronização',
  qualidade: 'Qualidade',
  ergonomia: 'Ergonomia',
};

export interface BenefitSourceKaizen {
  id: string;
  organizationId: string;
  title: string;
  type: string;
  /** Texto livre de kaizen_records.responsible. Não é usuário nem responsável financeiro. */
  responsible: string | null;
  projectId: string | null;
  operationalStatus: string;
}

export interface RegisteredBenefitLine {
  kaizenId: string;
  title: string;
  typeLabel: string;
  /** Null = campo ou registro ausente. Zero = valor gravado igual a zero. */
  annualValue: number | null;
  operationalStatus: string;
  operationalStatusLabel: string;
  inclusion: BenefitInclusion;
  inclusionNote: string;
  responsible: string | null;
  projectId: string | null;
  projectName: string;
  /** Null quando não há linha de ROI. Zero é investimento gravado igual a zero. */
  investment: number | null;
}

export interface ProjectBenefitRow {
  id: string;
  name: string;
  amount: number;
  pct: number;
}

/**
 * Uma linha por Kaizen da organização.
 * ROI duplicado conta uma vez, na primeira ocorrência.
 * Kaizen de outra organização não entra.
 */
export function buildRegisteredBenefits(
  kaizens: BenefitSourceKaizen[],
  roiRows: KaizenRoiAmountRow[],
  organizationId: string,
  projectNames: Record<string, string> = {},
): RegisteredBenefitLine[] {
  const roiByKaizen = new Map<string, KaizenRoiAmountRow>();
  for (const row of roiRows) {
    if (!row.kaizen_id || roiByKaizen.has(row.kaizen_id)) continue;
    roiByKaizen.set(row.kaizen_id, row);
  }

  return kaizens
    .filter((kaizen) => kaizen.organizationId === organizationId)
    .map((kaizen) => {
      const roi = roiByKaizen.get(kaizen.id);
      const projectId = kaizen.projectId || null;
      return {
        kaizenId: kaizen.id,
        title: kaizen.title,
        typeLabel: KAIZEN_IMPROVEMENT_TYPE_LABEL[kaizen.type] ?? kaizen.type.replace(/_/g, ' '),
        annualValue: roi && roi.annual_cost_saved != null ? Number(roi.annual_cost_saved) : null,
        operationalStatus: kaizen.operationalStatus,
        operationalStatusLabel: operationalStatusLabel(kaizen.operationalStatus),
        inclusion: benefitInclusion(kaizen.operationalStatus),
        inclusionNote: inclusionNote(benefitInclusion(kaizen.operationalStatus), kaizen.operationalStatus),
        responsible: kaizen.responsible?.trim() ? kaizen.responsible.trim() : null,
        projectId,
        projectName: projectId ? (projectNames[projectId] || 'Projeto sem nome') : 'Sem projeto',
        investment: roi && roi.total_investment != null ? Number(roi.total_investment) : null,
      };
    });
}

/**
 * Soma os annual_cost_saved gravados.
 * Carteira vazia = 0. Kaizens sem o campo = null, não zero.
 */
export function sumRegisteredAnnualBenefits(lines: RegisteredBenefitLine[]): number | null {
  if (lines.length === 0) return 0;
  const known = lines.filter((line) => line.annualValue != null);
  if (known.length === 0) return null;
  return known.reduce((sum, line) => sum + (line.annualValue ?? 0), 0);
}

export function buildAnnualSavingsByProject(lines: RegisteredBenefitLine[]): ProjectBenefitRow[] {
  const known = lines.filter((line) => line.annualValue != null);
  const total = known.reduce((sum, line) => sum + (line.annualValue ?? 0), 0);
  const byProject = new Map<string, { name: string; amount: number }>();
  for (const line of known) {
    const id = line.projectId || 'sem-projeto';
    const current = byProject.get(id) ?? { name: line.projectName, amount: 0 };
    current.amount += line.annualValue ?? 0;
    byProject.set(id, current);
  }
  return [...byProject.entries()]
    .map(([id, value]) => ({
      id,
      name: value.name,
      amount: value.amount,
      pct: total > 0 ? (value.amount / total) * 100 : 0,
    }))
    .sort((a, b) => b.amount - a.amount);
}

export interface GovernedBenefitSummary {
  activeAnnual: number | null;
  plannedAnnual: number | null;
  atRiskAnnual: number | null;
  activeAnnualBenefit: number | null;
  plannedAnnualBenefit: number | null;
  atRiskAnnualBenefit: number | null;
  activeKaizens: RegisteredBenefitLine[];
  plannedKaizens: RegisteredBenefitLine[];
  atRiskKaizens: RegisteredBenefitLine[];
  unknownKaizens: RegisteredBenefitLine[];
  /** Kaizens ativos cujo annual_cost_saved está informado. Universo do ROI. */
  includedKaizenIds: string[];
  /** Null quando o investimento dos Kaizens incluídos não está completo ou não é maior que zero. */
  activeInvestment: number | null;
  investmentAligned: boolean;
  roiPct: number | null;
  paybackMonths: number | null;
}

function sumKnownAnnual(lines: RegisteredBenefitLine[]): number | null {
  const known = lines.filter((line) => line.annualValue != null);
  if (known.length === 0) return null;
  return known.reduce((sum, line) => sum + (line.annualValue ?? 0), 0);
}

/**
 * Impacto ativo, potencial e benefício em risco.
 * ROI e investimento usam somente os Kaizens do impacto ativo com valor informado.
 * Se algum deles não tem investimento, o ROI não é calculado.
 */
export function governBenefits(lines: RegisteredBenefitLine[]): GovernedBenefitSummary {
  const activeKaizens = lines.filter((line) => line.inclusion === 'active');
  const plannedKaizens = lines.filter((line) => line.inclusion === 'planned');
  const atRiskKaizens = lines.filter((line) => line.inclusion === 'at_risk');
  const unknownKaizens = lines.filter((line) => line.inclusion === 'unrecognized');
  const activeAnnual = sumKnownAnnual(activeKaizens);
  const plannedAnnual = sumKnownAnnual(plannedKaizens);
  const atRiskAnnual = sumKnownAnnual(atRiskKaizens);
  const included = activeKaizens.filter((line) => line.annualValue != null);
  const investmentAligned = included.every((line) => line.investment != null);
  const investmentSum = included.reduce((sum, line) => sum + (line.investment ?? 0), 0);
  const roi = !investmentAligned || activeAnnual == null
    ? { roiPct: null, paybackMonths: null, investment: null }
    : computeConsolidatedRoi(activeAnnual, investmentSum);
  return {
    activeAnnual,
    plannedAnnual,
    atRiskAnnual,
    activeAnnualBenefit: activeAnnual,
    plannedAnnualBenefit: plannedAnnual,
    atRiskAnnualBenefit: atRiskAnnual,
    activeKaizens,
    plannedKaizens,
    atRiskKaizens,
    unknownKaizens,
    includedKaizenIds: included.map((line) => line.kaizenId),
    activeInvestment: roi.investment,
    investmentAligned,
    roiPct: roi.roiPct,
    paybackMonths: roi.paybackMonths,
  };
}

/** Classificação operacional do benefício. Não valida financeiramente o Kaizen. */
export function classifyKaizenBenefit(status: string): BenefitInclusion {
  return benefitInclusion(status);
}

/** Agregação canônica do Painel. Resultados e Executivo usam este retorno. */
export function aggregateKaizenBenefits(lines: RegisteredBenefitLine[]): GovernedBenefitSummary {
  return governBenefits(lines);
}

export function computeConsolidatedRoi(
  annualSavings: number,
  realInvestment: number,
): { roiPct: number | null; paybackMonths: number | null; investment: number | null } {
  const investment = realInvestment > 0 ? realInvestment : null;
  if (investment == null || !(annualSavings > 0)) {
    return { roiPct: null, paybackMonths: null, investment };
  }
  return {
    roiPct: (annualSavings / investment) * 100,
    paybackMonths: investment / (annualSavings / 12),
    investment,
  };
}
